<template>
  <div class="settings--content p-3">
    <form @submit.prevent="updateWidget">
      <div class="widget-builder-container">
        <div class="settings-container w-100 lg:w-[40%]">
          <div class="settings-content">
            <woot-avatar-uploader
              :label="
                $t('INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.AVATAR.LABEL')
              "
              :src="avatarUrl"
              delete-avatar
              @change="handleImageUpload"
              @onAvatarDelete="handleAvatarDelete"
            />
            <woot-input
              v-model.trim="websiteName"
              :class="{ error: $v.websiteName.$error }"
              :label="
                $t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_DISPLAY_NAME.LABEL')
              "
              :placeholder="
                $t(
                  'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WEBSITE_NAME.PLACE_HOLDER'
                )
              "
              :error="websiteNameValidationErrorMsg"
              @blur="$v.websiteName.$touch"
            />
            <woot-input
              v-model.trim="welcomeHeading"
              :label="
                $t(
                  'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WELCOME_HEADING.LABEL'
                )
              "
              :placeholder="
                $t(
                  'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WELCOME_HEADING.PLACE_HOLDER'
                )
              "
            />
            <woot-input
              v-model.trim="welcomeTagline"
              :label="
                $t(
                  'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WELCOME_TAGLINE.LABEL'
                )
              "
              :placeholder="
                $t(
                  'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WELCOME_TAGLINE.PLACE_HOLDER'
                )
              "
            />
            <label>
              {{
                $t('INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.REPLY_TIME.LABEL')
              }}
              <select v-model="replyTime">
                <option
                  v-for="option in getReplyTimeOptions"
                  :key="option.key"
                  :value="option.value"
                >
                  {{ option.text }}
                </option>
              </select>
            </label>
            <span class="image-notice">{{
              $t(
                'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.OPEN_CHAT_IMAGE.NOTICE'
              )
            }}</span>
            <online-image-uploader
              :label="
                $t(
                  'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.OPEN_CHAT_IMAGE.LABEL'
                )
              "
              :src="openChatUrl"
              delete-image
              :delete-title="
                $t(
                  'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.OPEN_CHAT_IMAGE.DELETE_BUTTON_TEXT'
                )
              "
              @change="handleOpenChatImageUpload"
              @onDelete="handleOpenChatImageDelete"
            />
            <label>
              {{
                $t(
                  'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WIDGET_COLOR_LABEL'
                )
              }}
              <woot-color-picker v-model="color" />
            </label>

            <label>
              {{
                $t(
                  'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WIDGET_BUBBLE_POSITION_LABEL'
                )
              }}

              <div class="flex gap-4">
                <woot-input
                  v-model="widgetBubblePosition.left"
                  type="number"
                  :label="
                    $t(
                      'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WIDGET_BUBBLE_POSITION.LEFT'
                    )
                  "
                  :placeholder="'px'"
                />
                <woot-input
                  v-model="widgetBubblePosition.right"
                  type="number"
                  :label="
                    $t(
                      'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WIDGET_BUBBLE_POSITION.RIGHT'
                    )
                  "
                  :placeholder="'px'"
                />
              </div>

              <div class="flex gap-4">
                <woot-input
                  v-model="widgetBubblePosition.top"
                  type="number"
                  :label="
                    $t(
                      'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WIDGET_BUBBLE_POSITION.TOP'
                    )
                  "
                  :placeholder="'px'"
                />
                <woot-input
                  v-model="widgetBubblePosition.bottom"
                  type="number"
                  :label="
                    $t(
                      'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WIDGET_BUBBLE_POSITION.BOTTOM'
                    )
                  "
                  :placeholder="'px'"
                />
              </div>
            </label>

            <woot-input
              v-model="widgetBubbleSize"
              type="number"
              class="w-1/2 pr-2"
              :label="$t('INBOX_MGMT.WIDGET_BUILDER.WIDGET_BUBBLE_SIZE_LABEL')"
              :placeholder="'px'"
              min="50"
            />

            <input-radio-group
              name="widget-bubble-type"
              :label="
                $t(
                  'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WIDGET_BUBBLE_TYPE_LABEL'
                )
              "
              :items="widgetBubbleTypes"
              :action="handleWidgetBubbleTypeChange"
            />

            <label>
              {{
                $t(
                  'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WIDGET_LOCALE_LABEL'
                )
              }}
              <select v-model="widgetLocale">
                <option :value="'he'">
                  {{
                    $t(
                      'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WIDGET_LOCALE.HE'
                    )
                  }}
                </option>
                <option :value="'en'">
                  {{
                    $t(
                      'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WIDGET_LOCALE.EN'
                    )
                  }}
                </option>
              </select>
            </label>

            <label>
              {{
                $t(
                  'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WIDGET_SHOW_AGENT_AVAILABILITY_LABEL'
                )
              }}
              <select v-model="showAgentAvailability">
                <option :value="true">
                  {{
                    $t(
                      'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WIDGET_SHOW_AGENT_AVAILABILITY.SHOW'
                    )
                  }}
                </option>
                <option :value="false">
                  {{
                    $t(
                      'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WIDGET_SHOW_AGENT_AVAILABILITY.HIDE'
                    )
                  }}
                </option>
              </select>
            </label>

            <woot-input
              v-model.trim="widgetBubbleLauncherTitle"
              :label="
                $t(
                  'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WIDGET_BUBBLE_LAUNCHER_TITLE.LABEL'
                )
              "
              :placeholder="
                $t(
                  'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WIDGET_BUBBLE_LAUNCHER_TITLE.PLACE_HOLDER'
                )
              "
            />
          </div>
        </div>
        <div class="widget-container w-100 lg:w-[60%]">
          <input-radio-group
            name="widget-view-options"
            :items="getWidgetViewOptions"
            :action="handleWidgetViewChange"
            :style="{ 'text-align': 'center' }"
          />
          <div
            v-if="isWidgetPreview"
            class="widget-preview"
            :style="{
              'flex-direction': widgetBubblePosition.top
                ? 'column-reverse'
                : 'column',
            }"
          >
            <Widget
              :welcome-heading="welcomeHeading"
              :welcome-tagline="welcomeTagline"
              :website-name="websiteName"
              :logo="avatarUrl"
              :open-chat-image="openChatUrl"
              is-online
              :reply-time="replyTime"
              :color="color"
              :widget-bubble-position="widgetBubblePosition"
              :widget-bubble-launcher-title="widgetBubbleLauncherTitle"
              :widget-bubble-type="widgetBubbleType"
              :show-agent-availability="showAgentAvailability"
              :widget-locale="widgetLocale"
              :contact-channels-enabled="contactChannelEnabled"
              :contact-channels="enabledContactChannels"
            />
          </div>
          <div v-else class="widget-script">
            <woot-code :script="widgetScript" />
          </div>
        </div>
      </div>
      <div class="my-4">
        <input-radio-group
          v-model="contactChannelEnabled"
          :label="$t('INBOX_MGMT.WIDGET_BUILDER.SETUP_CONTACT_CHANNELS_LABEL')"
          :items="contactChannelsEnabledOptions"
          :action="handleContactChannelEnabledChange"
        />

        <table
          v-if="contactChannelEnabled"
          class="table table-striped table-auto w-full"
        >
          <thead class="thead-dark">
            <tr>
              <th scope="col" />
              <th scope="col" />
              <th scope="col">
                {{
                  $t('INBOX_MGMT.WIDGET_BUILDER.SETUP_CONTACT_CHANNELS.TITLE')
                }}
              </th>
              <th scope="col">
                {{
                  $t(
                    'INBOX_MGMT.WIDGET_BUILDER.SETUP_CONTACT_CHANNELS.REQUIRED'
                  )
                }}
              </th>
              <th scope="col">
                {{
                  $t(
                    'INBOX_MGMT.WIDGET_BUILDER.SETUP_CONTACT_CHANNELS.DEFAULT_TEXT'
                  )
                }}
              </th>
            </tr>
          </thead>
          <setup-contact-channels
            :contact-channels="contactChannels"
            @update="handleContactChannelOptions"
            @drag-end="changeContactChannelsOrder"
          />
        </table>
      </div>

      <woot-submit-button
        class="submit-button"
        :button-text="
          $t('INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.UPDATE.BUTTON_TEXT')
        "
        :loading="uiFlags.isUpdating"
        :disabled="$v.$invalid || uiFlags.isUpdating"
      />
    </form>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import Widget from 'dashboard/modules/widget-preview/components/Widget.vue';
import OnlineImageUploader from '../../../../components/widgets/forms/OnlineImageUploader.vue';
import InputRadioGroup from './components/InputRadioGroup.vue';
import alertMixin from 'shared/mixins/alertMixin';
import { required } from 'vuelidate/lib/validators';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { LocalStorage } from 'shared/helpers/localStorage';
import SetupContactChannels from './components/SetupContactChannels.vue';
import { standardContactChannels } from '../../../../helper/inbox';

export default {
  components: {
    Widget,
    InputRadioGroup,
    OnlineImageUploader,
    SetupContactChannels,
  },
  mixins: [alertMixin],
  props: {
    inbox: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      isWidgetPreview: true,
      color: '#1f93ff',
      websiteName: '',
      welcomeHeading: '',
      welcomeTagline: '',
      replyTime: 'in_a_few_minutes',
      avatarFile: null,
      avatarUrl: '',
      openChatFile: null,
      openChatUrl: null,
      widgetBubblePosition: {},
      widgetBubbleLauncherTitle: this.$t(
        'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WIDGET_BUBBLE_LAUNCHER_TITLE.DEFAULT'
      ),
      widgetBubbleType: 'standard',
      widgetBubbleTypes: [
        {
          id: 'standard',
          title: this.$t(
            'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WIDGET_BUBBLE_TYPE.STANDARD'
          ),
          checked: true,
        },
        {
          id: 'expanded_bubble',
          title: this.$t(
            'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WIDGET_BUBBLE_TYPE.EXPANDED_BUBBLE'
          ),
          checked: false,
        },
      ],
      showAgentAvailability: true,
      widgetLocale: 'he',
      contactChannelEnabled: false,
      contactChannels: [...standardContactChannels],
      contactChannelsEnabledOptions: [
        {
          id: 'enabled',
          title: this.$t(
            'INBOX_MGMT.WIDGET_BUILDER.SETUP_CONTACT_CHANNELS.ENABLED'
          ),
          checked: true,
        },
        {
          id: 'disabled',
          title: this.$t(
            'INBOX_MGMT.WIDGET_BUILDER.SETUP_CONTACT_CHANNELS.DISABLED'
          ),
          checked: false,
        },
      ],
      widgetBubbleSize: 64,
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'inboxes/getUIFlags',
    }),
    storageKey() {
      return `${LOCAL_STORAGE_KEYS.WIDGET_BUILDER}${this.inbox.id}`;
    },
    widgetScript() {
      let options = {
        position: this.getPositionConfig(this.widgetBubblePosition),
        type: this.widgetBubbleType,
        launcherTitle: this.widgetBubbleLauncherTitle,
      };
      let script = this.inbox.web_widget_script;
      return (
        script.substring(0, 13) +
        this.$t('INBOX_MGMT.WIDGET_BUILDER.SCRIPT_SETTINGS', {
          options: JSON.stringify(options),
        }) +
        script.substring(13)
      );
    },
    getWidgetViewOptions() {
      return [
        {
          id: 'preview',
          title: this.$t(
            'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WIDGET_VIEW_OPTION.PREVIEW'
          ),
          checked: true,
        },
        {
          id: 'script',
          title: this.$t(
            'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WIDGET_VIEW_OPTION.SCRIPT'
          ),
          checked: false,
        },
      ];
    },
    getReplyTimeOptions() {
      return [
        {
          key: 'in_a_few_minutes',
          value: 'in_a_few_minutes',
          text: this.$t(
            'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.REPLY_TIME.IN_A_FEW_MINUTES'
          ),
        },
        {
          key: 'in_a_few_hours',
          value: 'in_a_few_hours',
          text: this.$t(
            'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.REPLY_TIME.IN_A_FEW_HOURS'
          ),
        },
        {
          key: 'in_a_day',
          value: 'in_a_day',
          text: this.$t(
            'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.REPLY_TIME.IN_A_DAY'
          ),
        },
      ];
    },
    websiteNameValidationErrorMsg() {
      return this.$v.websiteName.$error
        ? this.$t('INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WEBSITE_NAME.ERROR')
        : '';
    },
    enabledContactChannels() {
      return this.contactChannels.filter(
        item => item.enabled === true || item.enabled === 'true'
      );
    },
  },
  mounted() {
    this.setDefaults();
  },
  validations: {
    websiteName: { required },
  },
  methods: {
    setDefaults() {
      // Widget Settings
      const {
        name,
        welcome_title,
        welcome_tagline,
        widget_color,
        reply_time,
        avatar_url,
        chat_open_image_url,
        show_agent_availability,
        widget_locale,
        display_name,
        contact_channels_enabled,
        contact_channels,
        bubble_size,
      } = this.inbox;
      this.websiteName = display_name || name;
      this.welcomeHeading = welcome_title;
      this.welcomeTagline = welcome_tagline;
      this.color = widget_color;
      this.replyTime = reply_time;
      this.avatarUrl = avatar_url;
      this.openChatUrl = chat_open_image_url;
      this.showAgentAvailability = show_agent_availability;
      this.widgetLocale = widget_locale;
      this.contactChannelEnabled = contact_channels_enabled;
      this.contactChannelsEnabledOptions =
        this.contactChannelsEnabledOptions.map(item => {
          return {
            ...item,
            checked: contact_channels_enabled
              ? item.id === 'enabled'
              : item.id === 'disabled',
          };
        });
      this.contactChannels = this.buildContactChannelOptions(contact_channels);
      this.widgetBubbleSize = bubble_size || 64;

      const savedInformation = this.getSavedInboxInformation();
      if (savedInformation) {
        this.widgetBubbleTypes = this.widgetBubbleTypes.map(item => {
          if (item.id === savedInformation.type) {
            item.checked = true;
            this.widgetBubbleType = item.id;
          }
          return item;
        });
        if (typeof savedInformation.position !== 'object') {
          this.widgetBubblePosition = {};
        } else {
          const posConfig = this.getPositionConfig(savedInformation.position);
          this.widgetBubblePosition = posConfig;
        }
        this.widgetBubbleLauncherTitle =
          savedInformation.launcherTitle || 'Chat with us';
      }
    },
    handleWidgetBubbleTypeChange(item) {
      this.widgetBubbleType = item.id;
    },
    handleWidgetViewChange(item) {
      this.isWidgetPreview = item.id === 'preview';
    },
    handleImageUpload({ file, url }) {
      this.avatarFile = file;
      this.avatarUrl = url;
    },
    handleContactChannelEnabledChange(item) {
      this.contactChannelEnabled = item.id === 'enabled';
    },
    async handleOpenChatImageUpload({ file, url }) {
      const validImage = await this.checkImageDimensions(file);
      if (!validImage)
        return this.showAlert(
          this.$t(
            'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.OPEN_CHAT_IMAGE.ERROR.DIMENSIONS'
          )
        );

      this.openChatFile = file;
      this.openChatUrl = url;
      return null;
    },
    async handleOpenChatImageDelete() {
      try {
        await this.$store.dispatch(
          'inboxes/deleteOpenChatImage',
          this.inbox.id
        );
        this.openChatFile = null;
        this.openChatUrl = '';
        this.showAlert(
          this.$t(
            'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.OPEN_CHAT_IMAGE.DELETE.API.SUCCESS_MESSAGE'
          )
        );
      } catch (error) {
        this.showAlert(
          error.message
            ? error.message
            : this.$t(
                'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.OPEN_CHAT_IMAGE.DELETE.API.ERROR_MESSAGE'
              )
        );
      }
    },
    async handleAvatarDelete() {
      try {
        await this.$store.dispatch('inboxes/deleteInboxAvatar', this.inbox.id);
        this.avatarFile = null;
        this.avatarUrl = '';
        this.showAlert(
          this.$t(
            'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.AVATAR.DELETE.API.SUCCESS_MESSAGE'
          )
        );
      } catch (error) {
        this.showAlert(
          error.message
            ? error.message
            : this.$t(
                'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.AVATAR.DELETE.API.ERROR_MESSAGE'
              )
        );
      }
    },
    async updateWidget() {
      const bubbleSettings = {
        position: this.getPositionConfig(this.widgetBubblePosition),
        launcherTitle: this.widgetBubbleLauncherTitle,
        type: this.widgetBubbleType,
      };

      LocalStorage.set(this.storageKey, bubbleSettings);

      try {
        const payload = {
          id: this.inbox.id,
          channel: {
            widget_color: this.color,
            welcome_title: this.welcomeHeading,
            welcome_tagline: this.welcomeTagline,
            reply_time: this.replyTime,
            show_agent_availability: this.showAgentAvailability,
            widget_locale: this.widgetLocale,
            bubble_size: this.widgetBubbleSize,
            display_name: this.websiteName,
            contact_channels_enabled: this.contactChannelEnabled,
            contact_channels: this.buildUpdateParams(),
          },
        };
        if (this.avatarFile) {
          payload.avatar = this.avatarFile;
        }
        if (this.openChatFile) {
          payload.chat_open_image = this.openChatFile;
        }
        await this.$store.dispatch('inboxes/updateInbox', payload);
        this.showAlert(
          this.$t(
            'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.UPDATE.API.SUCCESS_MESSAGE'
          )
        );
      } catch (error) {
        this.showAlert(
          error.message ||
            this.$t(
              'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.UPDATE.API.ERROR_MESSAGE'
            )
        );
      }
    },
    getSavedInboxInformation() {
      return LocalStorage.get(this.storageKey);
    },
    getPositionConfig(info) {
      let posConfig = { ...info };
      Object.keys(posConfig).forEach(k => {
        if (
          ['left', 'right', 'top', 'bottom'].indexOf(k) === -1 ||
          posConfig[k] === '' ||
          posConfig[k] === undefined ||
          Number.isNaN(parseInt(posConfig[k], 10))
        ) {
          delete posConfig[k];
        }
      });
      return posConfig;
    },
    buildContactChannelOptions(existOptions) {
      let contactChannelOptions = [];
      if (!existOptions || existOptions.length === 0) {
        contactChannelOptions = standardContactChannels;
      } else {
        contactChannelOptions = existOptions.concat(
          standardContactChannels.filter(
            item => !existOptions.find(i => i.key === item.key)
          )
        );
      }

      const finalOptions = contactChannelOptions.map(item => {
        const existOption = existOptions.find(i => i.key === item.key) || item;

        return {
          enabled:
            existOption.enabled === 'true' || existOption.enabled === true,
          title: existOption.title === 'null' ? '' : existOption.title,
          value: existOption.value === 'null' ? '' : existOption.value,
          default_text:
            existOption.default_text === 'null' ? '' : existOption.default_text,
          display_text:
            existOption.display_text === 'null' ||
            existOption.display_text === '' ||
            existOption.display_text === 'undefined'
              ? existOption.default_text
              : existOption.display_text,
          input_placeholder:
            existOption.input_placeholder === 'null'
              ? ''
              : existOption.input_placeholder,
          key: existOption.key,
          action_link: existOption.action_link,
        };
      });
      return finalOptions;
    },
    // d99d
    checkImageDimensions(file) {
      return new Promise(resolve => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = e => {
          const img = new Image();
          img.src = e.target.result;
          img.onload = () => {
            const width = img.naturalWidth;
            const height = img.naturalHeight;

            if (width === 64 && height === 64) {
              resolve(true);
            } else {
              resolve(false);
            }
          };
        };
      });
    },

    handleContactChannelOptions(event, type, item) {
      this.contactChannels.find(i => i.key === item.key)[type] = !item[type];
    },

    changeContactChannelsOrder(updatedContactChannelOptions) {
      this.contactChannels = updatedContactChannelOptions;
    },
    buildUpdateParams() {
      let params = this.contactChannels.map(item => {
        let value = '';
        if (item.key === 'EMAIL') {
          value = this.validateEmail(item.value.trim())
            ? item.value.trim()
            : '';
        } else if (
          item.key === 'PHONE' ||
          item.key === 'WHATSAPP' ||
          item.key === 'SMS' ||
          item.key === 'PHONECALL'
        ) {
          value = this.validatePhone(item.value.trim())
            ? item.value.trim()
            : '';
        } else {
          value = item.value.trim();
        }
        return {
          ...item,
          value,
        };
      });
      params = params.map(item => {
        return {
          ...item,
          display_text:
            !item.display_text || item.display_text === ''
              ? item.default_text
              : item.display_text,
          enabled:
            (item.enabled === true || item.enabled === 'true') &&
            item.value !== '',
        };
      });

      return params;
    },
    validateEmail(email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email);
    },
    validatePhone(phone) {
      const phoneRegex = /^\+?[0-9]{10,15}$/;
      return phoneRegex.test(phone);
    },
    // .
  },
};
</script>

<style lang="scss" scoped>
@import '~dashboard/assets/scss/woot';

.widget-builder-container {
  display: flex;
  flex-direction: row;
  @include breakpoint(900px down) {
    flex-direction: column;
  }
}

.settings-container {
  .settings-content {
    padding: var(--space-normal) var(--space-zero) var(--space-zero);
    overflow-y: scroll;
    min-height: 100%;

    .submit-button {
      margin-top: var(--space-normal);
    }
  }
}

.widget-container {
  .widget-preview {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-end;
    min-height: 42.5rem;
    margin: var(--space-zero) var(--space-two) var(--space-two) var(--space-two);
    padding: var(--space-one) var(--space-one) var(--space-one) var(--space-one);
    @apply bg-slate-50 dark:bg-slate-700;

    @include breakpoint(500px down) {
      background: none;
    }
  }
  .widget-script {
    @apply mx-5 p-2.5 bg-slate-50 dark:bg-slate-700;
  }
}

.image-notice {
  color: green;
  font-size: 13px;
}
</style>
